import { CSSProperties, ReactElement, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Select, Spin, Typography } from 'antd'
import uniqBy from 'lodash/uniqBy'

import { getUserOrganizations } from 'api/Cerebro/orgScoped/userApi'
import { Flex } from 'components/core'
import { useTypeahead } from 'hooks'
import { theme } from 'styles/theme'
import { Organization } from 'types'

import * as styles from './styles.scss'

const { Text } = Typography

const PREFETCH_RESULTS_PER_QUERY = 100
const SEARCH_RESULTS_PER_QUERY = 50

function buildGetOrganizationsParams(
    impersonateAccess: boolean,
    params: Record<string, unknown>
): Record<string, unknown> {
    return impersonateAccess ? { ...params, impersonate_access: true } : params
}

interface SerializableOrg {
    id: string
    locked?: boolean
    label?: string
}

const buildSerializableOrgLabel = (
    orgName?: string,
    orgAgencyName?: string
): string => {
    if (!orgName) {
        return 'Loading...'
    }
    const orgAgency = orgAgencyName ? ` | ${orgAgencyName}` : ''
    return `${orgName}${orgAgency}`
}

interface OrgLabelProps {
    serializable: SerializableOrg
    showOrgId: boolean
}

function OrgLabel({ serializable, showOrgId }: OrgLabelProps): ReactElement {
    const { id, label } = serializable

    if (!showOrgId) {
        return (
            <Flex className={styles.display}>
                <Text className={styles.label}>{label}</Text>
            </Flex>
        )
    }

    return (
        <Flex gap={8} className={styles.display}>
            <div
                style={{
                    width: 32,
                    textAlign: 'right',
                }}
            >
                <Text
                    style={{
                        color: theme.colors.textLight,
                        fontWeight: theme.fontWeight.light,
                    }}
                >
                    {id}
                </Text>
            </div>
            <span style={{ color: theme.colors.textExtraLight }}>|</span>
            <Flex style={{ flex: 1 }}>
                <Text className={styles.label}>{label}</Text>
            </Flex>
        </Flex>
    )
}

interface SelectOption {
    label: ReactElement | string
    value?: string
    locked?: boolean
    searchable?: string
}

interface OrganizationSearchSelectProps {
    selectedOrgId: string
    onChange: (value: string) => void
    className?: string
    style?: CSSProperties
    prefetchRecordLimit?: number
    impersonateAccess?: boolean
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
    autoFocus?: boolean
    'aria-labelledby'?: string
}

function OrganizationSearchSelect({
    selectedOrgId,
    onChange,
    className,
    style,
    prefetchRecordLimit = PREFETCH_RESULTS_PER_QUERY,
    impersonateAccess = false,
    getPopupContainer,
    autoFocus,
    'aria-labelledby': ariaLabelledBy,
}: OrganizationSearchSelectProps): ReactElement {
    const { data: selectedOrgData, isLoading: isLoadingSelectedOrg } = useQuery(
        {
            queryKey: ['organization', selectedOrgId, impersonateAccess],
            queryFn: () =>
                getUserOrganizations(
                    buildGetOrganizationsParams(impersonateAccess, {
                        id: selectedOrgId,
                    })
                ),
            enabled: !!selectedOrgId,
        }
    )

    const selectedOrg: Organization | undefined =
        selectedOrgData?.data.results?.[0]

    const defaultOptions = []
    if (selectedOrg) {
        defaultOptions.push(selectedOrg)
    }

    const apiSearchFunc = useCallback(
        (query: string) =>
            getUserOrganizations(
                buildGetOrganizationsParams(impersonateAccess, {
                    limit: SEARCH_RESULTS_PER_QUERY,
                    name_or_id__matches: query,
                    ordering: 'locked,name,-created_date',
                })
            ),
        [impersonateAccess]
    )

    const [options, loading, onSearch, resetLoadingState] =
        useTypeahead<Organization>({
            apiSearchFunc,
            optionFormatter: ({
                id: organizationId,
                name: organizationName,
                locked,
                agency,
            }) => {
                const serializable = {
                    id: organizationId,
                    locked,
                    label: buildSerializableOrgLabel(
                        organizationName,
                        typeof agency === 'object' ? agency?.name : undefined
                    ),
                }
                return {
                    value: serializable.id,
                    label: impersonateAccess
                        ? `${organizationId} ${organizationName}`
                        : `${organizationName}`,
                    serializable,
                }
            },
            defaultOptions,
            prefetchApiFunc: useCallback(
                () =>
                    getUserOrganizations(
                        buildGetOrganizationsParams(impersonateAccess, {
                            limit: prefetchRecordLimit,
                            ordering: 'locked,name,-created_date',
                        })
                    ),
                [impersonateAccess, prefetchRecordLimit]
            ),
        })

    const isLoading = loading || isLoadingSelectedOrg

    // Construct the proper objects to be used in the select
    const searchOptions: SelectOption[] = options.map((option) => {
        return {
            value: option.serializable.id,
            label: (
                <OrgLabel
                    serializable={option.serializable}
                    showOrgId={impersonateAccess}
                />
            ),
            locked: option.serializable.locked ?? false,
            searchable: option.label,
        }
    })

    const allOptions = uniqBy([...searchOptions], 'value')
    const activeOptions = allOptions.filter((option) => !option.locked)
    const lockedOptions = allOptions.filter((option) => option.locked)

    const categorizedOptions = [
        ...(activeOptions.length > 0
            ? [{ label: 'Active', options: activeOptions }]
            : []),
        ...(lockedOptions.length > 0
            ? [{ label: 'Locked', options: lockedOptions }]
            : []),
    ]

    return (
        <Select<string, SelectOption>
            autoFocus={autoFocus}
            aria-labelledby={ariaLabelledBy}
            getPopupContainer={getPopupContainer}
            onKeyDown={(e) => {
                // Prevent Menu from handling arrow keys
                if (
                    [
                        'ArrowUp',
                        'ArrowDown',
                        'ArrowLeft',
                        'ArrowRight',
                    ].includes(e.key)
                ) {
                    e.stopPropagation()
                }
            }}
            className={className}
            style={{ ...style, width: '100%' }}
            optionFilterProp="searchable"
            optionLabelProp="label"
            placeholder="Select an Organization"
            loading={isLoading}
            notFoundContent={isLoading ? <Spin size="small" /> : null}
            onChange={onChange}
            onSearch={onSearch}
            onSelect={resetLoadingState}
            showSearch
            value={selectedOrgId}
            options={categorizedOptions}
        />
    )
}

export default OrganizationSearchSelect
